import type { PollPolicy } from '@nucleus-commerce/admin-kit/jobs';
import type { ExportKind, ImportKind, JobStatus } from '@nucleus-commerce/api-client/admin';

/**
 * Jobs run on the worker. The screen polls a job it just created every 2 to 3 seconds while it is
 * queued or running, stops at completed/failed, and after 90 s hands over to a manual check with
 * a 10 s cooldown (ADR 0029), with the interval tightened from the ADR 0042 defaults.
 */
export const JOB_POLL_POLICY: PollPolicy = {
  initialMs: 2_000,
  maxMs: 3_000,
  factor: 1.25,
  timeoutMs: 90_000,
  cooldownMs: 10_000,
};

export interface TrackedJob {
  id: string;
  /** When the job was created here; the polling timeout counts from it. */
  startedAt: number;
}

export function isJobActive(status: JobStatus): boolean {
  return status === 'queued' || status === 'running';
}

export function isJobTerminal(job: { status: JobStatus }): boolean {
  return !isJobActive(job.status);
}

/** `filename="orders-2026-09-29-a1b2c3.csv"` or RFC 5987 `filename*=UTF-8''…`; null when absent. */
export function filenameFromDisposition(header: string | null | undefined): string | null {
  if (!header) return null;
  const extended = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(header);
  if (extended?.[1]) {
    try {
      return sanitizeFilename(decodeURIComponent(extended[1].trim()));
    } catch {
      return null;
    }
  }
  const plain = /filename\s*=\s*"([^"]*)"|filename\s*=\s*([^;]+)/.exec(header);
  const name = plain?.[1] ?? plain?.[2]?.trim();
  return name ? sanitizeFilename(name) : null;
}

function sanitizeFilename(name: string): string | null {
  const base = name.split(/[\\/]/).pop()?.trim() ?? '';
  return base.length > 0 ? base : null;
}

/** Used when the response carries no readable Content-Disposition (CORS can hide it). */
/** Same spelling as the backend's attachment name, used when CORS hides the header. */
export function fallbackSampleFilename(kind: ImportKind): string {
  return `nucleus-sample-${kind.replace(/_csv$/, '')}.csv`;
}

export function fallbackExportFilename(kind: ExportKind, day: string): string {
  return `${kind.replace(/_csv$/, '')}-${day}.csv`;
}
